import { useQuery } from "@tanstack/react-query";

import { useContext, useMemo } from "react";

import { UserContext } from "@/contexts/user-context";

import { createLookupMap } from "@/lib/array-utils";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";

import { LocationDetail } from "@/types/location";
import { ProviderSummary } from "@/types/provider";

import { calendarQueryOptions } from "@/query/check-in/calendar/calendar-query";
import { useTenantStore } from "@/stores/tenant-store";

// Module-level stable references so that callers depending on the return
// values as useEffect/useMemo deps don't re-fire just because the query
// hasn't resolved yet (which would otherwise produce a fresh `[]` literal
// on every render).
const EMPTY_LOCATIONS: LocationDetail[] = [];

interface UseLocationsOptions {
  seeAllLocations?: boolean;
  // Optional override for when called inside UserProvider (before context is available)
  currentUser?: ProviderSummary | null;
  // Read-side surfaces (analytics dashboards) need to surface archived
  // locations so users can filter historical data on a closed location.
  // Write-side surfaces (book appointment, create charge) should leave this
  // off — you can't operate at a closed location.
  includeArchived?: boolean;
  // Set false to defer the fetch (e.g. until a dialog is opened).
  enabled?: boolean;
}

interface UseLocationsResult {
  locations: LocationDetail[];
  isLocationsLoading: boolean;
  locationNameById: Record<string, string>;
  allLocationNameById: Record<string, string>;
}

export function useLocations({
  seeAllLocations = false,
  currentUser: currentUserOverride,
  includeArchived = false,
  enabled = true,
}: UseLocationsOptions = {}): UseLocationsResult {
  const { getCurrentTenant } = useTenantStore();
  const userContext = useContext(UserContext);
  // Use override if provided (for use inside UserProvider), otherwise get from context
  const currentUser = currentUserOverride ?? userContext?.currentUser;
  // Read the raw value directly — the `|| []` fallback used to live here
  // but that produced a fresh array reference every render, which
  // destabilized `locations` below and triggered an infinite render loop
  // in any consumer using `locations` as a useEffect dep.
  const visibleLocationIds = currentUser?.visibleLocationIds;

  const { data: rawLocations, isPending: isLocationsLoading } = useQuery({
    ...calendarQueryOptions.locations(includeArchived),
    select: (data) => {
      if (!data) return EMPTY_LOCATIONS;
      return includeArchived ? data : data.filter((l) => !l.isArchived);
    },
    // Only fetch when we have user and tenant from URL (avoids 400 "Tenant determination failed")
    enabled:
      enabled &&
      !!currentUser &&
      !!getCurrentTenant() &&
      getCurrentTenant() !== "login",
  });
  // Fall back to a module-level stable reference instead of a fresh `[]`
  // literal so `locations` doesn't become a new array every render while
  // the query is pending.
  const allLocations = rawLocations ?? EMPTY_LOCATIONS;

  const canSeeAllLocations = hasPermission(
    currentUser,
    PERMISSIONS.LOCATIONS_SEE_ALL,
  );

  const locations = useMemo(() => {
    if (seeAllLocations || canSeeAllLocations) return allLocations;

    if (visibleLocationIds && visibleLocationIds.length > 0) {
      return allLocations.filter((l) => visibleLocationIds.includes(l.id));
    }

    return allLocations;
  }, [allLocations, seeAllLocations, canSeeAllLocations, visibleLocationIds]);

  const locationNameById = useMemo(
    () =>
      createLookupMap(
        locations,
        (loc) => loc.id,
        (loc) => loc.name,
      ),
    [locations],
  );

  // Only expose all location names when explicitly requested or user has permission
  const allLocationNameById = useMemo(
    () =>
      createLookupMap(
        seeAllLocations || canSeeAllLocations ? allLocations : locations,
        (loc) => loc.id,
        (loc) => loc.name,
      ),
    [allLocations, locations, seeAllLocations, canSeeAllLocations],
  );

  return {
    locations,
    isLocationsLoading,
    locationNameById,
    allLocationNameById,
  };
}
